import { create } from "zustand";
import { isAxiosError } from "axios";
import * as authApi from "@/api/auth";
import { endImpersonation } from "@/api/admin";
import { queryClient } from "@/lib/queryClient";
import { isMfaChallenge } from "@/types/auth";
import type {
  AuthState,
  LoginResponse,
  LoginSuccess,
  User,
} from "@/types/auth";

const AUTH_TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const SUPERADMIN_ACCESS_TOKEN_KEY = "superadmin_access_token";
const SUPERADMIN_REFRESH_TOKEN_KEY = "superadmin_refresh_token";

/**
 * Best-effort: if the user is currently impersonating a tenant, close the
 * backend impersonation session BEFORE local tokens are cleared (e.g. on
 * logout). The end endpoint is gated `IsSuperAdmin`, so the active auth token
 * is restored to the superadmin token first (the impersonation token would
 * 403). Mirrors the token-restore pattern in AppShell.handleExitImpersonation.
 *
 * Never throws — exiting/logging out must always succeed locally. The backend
 * 1h auto-expire (BUG-140) is the real guarantee; this just narrows the window.
 */
async function endImpersonationIfActive(): Promise<void> {
  const superadminAccess = localStorage.getItem(SUPERADMIN_ACCESS_TOKEN_KEY);
  if (!superadminAccess) return;
  // Restore the superadmin token so the end request below carries it. The
  // axios interceptor reads auth_token fresh from localStorage per request.
  localStorage.setItem(AUTH_TOKEN_KEY, superadminAccess);
  localStorage.setItem(
    REFRESH_TOKEN_KEY,
    localStorage.getItem(SUPERADMIN_REFRESH_TOKEN_KEY) ?? "",
  );
  localStorage.removeItem(SUPERADMIN_ACCESS_TOKEN_KEY);
  localStorage.removeItem(SUPERADMIN_REFRESH_TOKEN_KEY);
  try {
    await endImpersonation();
  } catch {
    // Ignore — logout must always proceed locally.
  }
}

/**
 * Best-effort: drop the `docs_access` cookie on logout (TASK-4770).
 *
 * Hygiene, not the security boundary — the cookie carries the session id, so
 * logging out revokes docs access server-side regardless. Called BEFORE the
 * local tokens are cleared, because the endpoint is `IsAuthenticated`.
 *
 * Never throws: logout must always proceed locally, same contract as
 * `endImpersonationIfActive` above.
 */
async function clearDocsSessionQuietly(): Promise<void> {
  try {
    await authApi.clearDocsSession();
  } catch {
    // Ignore — logout must always proceed locally.
  }
}

/**
 * The result of a login attempt.
 *
 * A successful credential check may still not authenticate the user: if the
 * account has MFA enabled the backend returns an interim challenge instead of
 * tokens. The caller must then complete `verifyMfa`.
 */
export type LoginResult =
  | { status: "authenticated"; user: User }
  | { status: "mfa_required"; mfaToken: string };

interface AuthActions {
  /**
   * Calls the login API. On a normal (non-MFA) account, persists tokens and
   * marks the session authenticated. On an MFA-enabled account, returns the
   * interim challenge WITHOUT persisting anything — the caller must then call
   * `verifyMfa` to finish.
   */
  login: (email: string, password: string) => Promise<LoginResult>;
  /**
   * Completes an SSO callback login from an already-fetched exchange payload
   * (the SAME union as password login). On a `LoginSuccess` it persists tokens
   * (reusing `persistAuthenticatedSession` so BUG-186 cache-clear happens) and
   * marks the session authenticated. On an MFA challenge it returns the interim
   * challenge WITHOUT persisting anything — the caller then completes `verifyMfa`
   * exactly as the password-login MFA branch does.
   */
  completeSso: (response: LoginResponse) => LoginResult;
  /**
   * Completes an MFA-gated login: exchanges the interim `mfaToken` + a TOTP or
   * recovery code for real tokens, persists them, and marks the session
   * authenticated. Returns the authenticated user.
   */
  verifyMfa: (mfaToken: string, code: string) => Promise<User>;
  /**
   * Ends any active impersonation session (best-effort), then clears the auth
   * token from localStorage and resets store state.
   */
  logout: () => Promise<void>;
  /**
   * Reads auth_token from localStorage on app startup, restores auth state,
   * and fetches the current user profile from the API.
   */
  initFromStorage: () => Promise<void>;
  /**
   * Updates the user object in the store in place (e.g. after profile update).
   */
  updateUser: (user: User) => void;
}

/**
 * Persists a successful login/verify payload and marks the session
 * authenticated. Shared by `login` (non-MFA) and `verifyMfa` so the two paths
 * can never drift in how they establish identity.
 *
 * Returns the authenticated user for the caller to route on.
 */
function persistAuthenticatedSession(
  response: LoginSuccess,
  set: (partial: Partial<AuthState>) => void,
): User {
  // Persist BOTH tokens: the short-lived access token and the long-lived
  // refresh token used by the api client to silently renew access.
  localStorage.setItem(AUTH_TOKEN_KEY, response.access);
  localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh);
  // BUG-203: an explicit login is a fresh, authoritative identity. Drop any
  // dangling superadmin-impersonation backup keys from a previous session so
  // they can never later promote-and-bounce the new identity. (An active
  // impersonation is always started AFTER login, so clearing here is safe.)
  localStorage.removeItem(SUPERADMIN_ACCESS_TOKEN_KEY);
  localStorage.removeItem(SUPERADMIN_REFRESH_TOKEN_KEY);
  // BUG-186: drop any cached queries from a PREVIOUS identity so the new user
  // never sees the prior account's data before a refetch (role-switch is a
  // client-side navigation, not a full reload). Cleared on both login and
  // logout to cover login-without-an-explicit-logout.
  queryClient.clear();
  set({
    user: response.user,
    token: response.access,
    isAuthenticated: true,
  });
  return response.user;
}

export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  user: null,
  token: null,
  isAuthenticated: false,

  login: async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    // MFA-enabled account: the backend returned an interim challenge instead of
    // tokens. Do NOT persist anything — the interim mfa_token is not an auth
    // token. Hand the challenge back so the caller can prompt for a code.
    if (isMfaChallenge(response)) {
      return { status: "mfa_required", mfaToken: response.mfa_token };
    }
    const user = persistAuthenticatedSession(response, set);
    return { status: "authenticated", user };
  },

  completeSso: (response: LoginResponse) => {
    // The SSO exchange returns the same union as password login. Mirror the
    // `login` branching so both paths establish identity identically (and the
    // BUG-186 query-cache clear inside `persistAuthenticatedSession` runs).
    if (isMfaChallenge(response)) {
      return { status: "mfa_required", mfaToken: response.mfa_token };
    }
    const user = persistAuthenticatedSession(response, set);
    return { status: "authenticated", user };
  },

  verifyMfa: async (mfaToken: string, code: string) => {
    const response = await authApi.verifyMfa(mfaToken, code);
    return persistAuthenticatedSession(response, set);
  },

  logout: async () => {
    // Close the backend impersonation session first (under the superadmin
    // identity) so logging out while impersonating does not leave it "active".
    await endImpersonationIfActive();
    // Drop docs access too, while the token is still present.
    await clearDocsSessionQuietly();
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // Purge all cached query data so the next user can't see this account's
    // tenant/billing data before a refetch.
    queryClient.clear();
    set({ user: null, token: null, isAuthenticated: false });
  },

  initFromStorage: async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    set({ token, isAuthenticated: true, user: null });
    try {
      const user = await authApi.getMe();
      set({ user });
      // BUG-203: resolve a single deterministic active session per tab.
      //
      // The superadmin-impersonation backup keys (`superadmin_*`) only exist
      // WHILE a superadmin is impersonating a tenant — at which point the active
      // `auth_token` belongs to the impersonated (non-superadmin) user. If the
      // active session resolves to a real `superadmin`, there is NO active
      // impersonation, so any lingering backup keys are dangling (e.g. a
      // re-login as superadmin while an old impersonation was never exited).
      // Left in place they would let an incidental logout/exit promote the
      // stale backup and silently bounce the identity. Clearing them here —
      // only on an explicit init/login boundary, never on resize/nav — makes
      // the active token authoritative and stops the bounce.
      if (user.role === "superadmin") {
        localStorage.removeItem(SUPERADMIN_ACCESS_TOKEN_KEY);
        localStorage.removeItem(SUPERADMIN_REFRESH_TOKEN_KEY);
      }
    } catch (error) {
      // Only a genuine auth failure (401/403) means the session is dead. The
      // api client already attempts a silent refresh on 401, so reaching here
      // with 401/403 means refresh could not recover it — clear both tokens and
      // force re-login.
      //
      // For any other error (network failure, 5xx, or a non-auth 4xx) the
      // tokens are still valid: keep them and leave the session intact so a
      // later retry can resolve the user. Never log the user out on a transient
      // error (BUG-174).
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 401 || status === 403) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        set({ token: null, isAuthenticated: false, user: null });
      }
      // Otherwise: keep token + isAuthenticated as-is, user remains null.
    }
  },

  updateUser: (user: User) => set({ user }),
}));
